import {
  createDepositReservation,
} from '@features/booking/api/reservations';
import {
  createWlDepositStripe,
} from '@features/booking/api/waitingList';
import type { DepositGrabPayload } from '@features/booking/api/reservations';
import { getStripePromise } from '@features/booking/hooks/useStripeV3';
import { handle3dsChallenge } from '@features/booking/hooks/handle3dsChallenge';
import { getStripePublishableKeyForTokenAccount } from '@features/booking/utils/amexChange';
import { env } from '@core/config/env';
import * as Sentry from '@sentry/react';
import type { BaseReservationFields, CommitContext, CommitDerived } from './shared';
import { persistSuccessAndAdvance, buildDepositFields } from './shared';

/**
 * Stripe deposit/no-show reservation path — with 3DS support.
 *
 * Key invariants (see plan §payment-branches §3 + §3DS-SNAPSHOT §2):
 * - Stripe pubkey resolution goes through getStripePublishableKeyForTokenAccount,
 *   which routes AMEX no_show through platform credentials when stripeTokenAccount
 *   is set. Sentry breadcrumb fires when AMEX + no_show + missing stripeTokenAccount.
 * - threeDsResult.reservationId is the confirmed ID; response.reservationId is
 *   pre-3DS and unusable for state writes.
 * - There is no client-side card-decline fallback: the backend now creates the
 *   reservation for declined no_show cards on the normal deposit-grab call.
 *   Errors propagate to the outer commit catch where the UI maps them.
 */
export async function commitDepositStripe(
  ctx: CommitContext,
  derived: CommitDerived,
  basePayload: BaseReservationFields,
): Promise<void> {
  const { booking, restaurant } = ctx;
  const { isWaitingList, effectiveGuaranteeType } = derived;

  // Narrow types for the deposit endpoints. depositStripe is only reached with
  // effectiveGuaranteeType === 'deposit' | 'no_show' (caller-enforced).
  const guaranteeTypeNarrow = effectiveGuaranteeType as 'deposit' | 'no_show';
  const depositBase: DepositGrabPayload = {
    ...basePayload,
    date: basePayload.date as string,
    menu: basePayload.menu as number,
    ...buildDepositFields(ctx),
    type: guaranteeTypeNarrow,
    cardToken: booking.cardToken!,
    paymentMethod: booking.paymentMethod || 'unknown',
  };

  // No client-side card-decline fallback: the backend now creates the
  // reservation when a no_show card is declined. Errors propagate to the outer
  // commit catch where the UI maps them.
  const response = isWaitingList
    ? await createWlDepositStripe(
        ctx.currentLanguage,
        { ...depositBase, date: booking.wlDates },
      )
    : await createDepositReservation(ctx.currentLanguage, depositBase);

  const was3ds = !!response.requires_action;

  // Diagnose AMEX no_show flows that arrived without a resolved
  // stripeTokenAccount — they cannot route through the right Stripe key.
  // Sentry breadcrumb category: 'stripe-token-routing'. (Fires whether or not
  // 3DS triggers — the misconfiguration is about routing the pubkey, which
  // matters for both v3 challenges and non-3DS validations.)
  if (
    booking.stripeTokenAccount === null &&
    booking.paymentMethod === 'American Express' &&
    effectiveGuaranteeType === 'no_show'
  ) {
    Sentry.addBreadcrumb({
      category: 'stripe-token-routing',
      message: 'Missing stripeTokenAccount for AMEX no_show on deposit path',
      level: 'warning',
      data: {
        restaurant: restaurant.id,
        paymentMethod: booking.paymentMethod,
        guaranteeType: effectiveGuaranteeType,
      },
    });
  }
  const stripePublishableKey = getStripePublishableKeyForTokenAccount({
    tokenAccount: booking.stripeTokenAccount,
    restaurantPublishableKey: restaurant.publicKeyPayment,
    platformPublishableKey: env.stripeFallbackKey,
  });

  // T30 overlay: only fire on3dsStart/on3dsEnd when 3DS will actually
  // happen. handle3dsChallenge returns null for non-3DS responses without
  // any user-visible work; firing the callback unconditionally would flash
  // the overlay on plain card payments.
  if (was3ds) ctx.on3dsStart?.();
  let threeDsResult;
  try {
    threeDsResult = await handle3dsChallenge(
      response,
      getStripePromise(stripePublishableKey),
    );
  } finally {
    if (was3ds) ctx.on3dsEnd?.();
  }
  if (threeDsResult) {
    persistSuccessAndAdvance(
      ctx,
      derived,
      threeDsResult.reservationId,
      response.uniqueId,
      guaranteeTypeNarrow,
    );
    return;
  }

  persistSuccessAndAdvance(
    ctx,
    derived,
    response.reservationId,
    response.uniqueId,
    guaranteeTypeNarrow,
  );
}
